import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Warning,
  ArrowLeft,
  ArrowRight,
  Briefcase,
  Buildings,
  CalendarBlank,
  Check,
  CheckCircle,
  Exam,
  CopySimple,
  Eye,
  EyeClosed,
  FileText,
  Key,
  SpinnerGap,
  Microphone,
  Sparkle,
  TextT,
  UploadSimple,
  User,
  X,
} from '@phosphor-icons/react';
import { useEndClients, type NeuraxEndClient } from '@/lib/end-clients/useEndClients';
import {
  useScheduleSession,
  type ScheduleSessionInput,
  type SessionType,
} from '@/lib/sessions/useScheduleSession';
import { useUploadMaterials } from '@/lib/sessions/useUploadMaterials';
import {
  useMaterialsParseStatus,
  type MaterialParseEntry,
} from '@/lib/sessions/useMaterialsParseStatus';
import {
  additionalMaterialTypeLabel,
  toAdditionalMaterialInputs,
  validateAdditionalMaterials,
  type AdditionalMaterialDraft,
} from '@/lib/sessions/additionalMaterials';
import { cn } from '@/lib/utils/cn';
import { AdditionalMaterialsSection } from './AdditionalMaterialsSection';
import { formatFileSize } from '@/lib/utils/formatFileSize';
import { FileDropZone, Label, SourceToggle } from './MaterialFields';

export interface WizardDemoMode {
  validUntilDays: number;
  assignedToEmployeeId: string;
  assignedToEmployeeEmail: string;
  /** Human-readable label of who will activate the demo (shown in the
   *  ConfirmStep instead of the synthetic candidate email). */
  recipientLabel: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  sessionType: SessionType;
  defaultEndClientId?: string;
  /** Non-null when the wizard is being used to issue an employee_demo license.
   *  The end_client referenced by `defaultEndClientId` must have is_demo=true. */
  demoMode?: WizardDemoMode;
}

type Step = 1 | 2 | 3;

const STEPS: { num: Step; label: string; icon: ReactNode }[] = [
  { num: 1, label: 'Details', icon: <Exam className="h-3.5 w-3.5" /> },
  { num: 2, label: 'Materials', icon: <UploadSimple className="h-3.5 w-3.5" /> },
  { num: 3, label: 'Confirm', icon: <Check className="h-3.5 w-3.5" /> },
];

export function ScheduleSessionWizard({
  open,
  onClose,
  sessionType,
  defaultEndClientId,
  demoMode,
}: Props) {
  // Demo flow needs is_demo=true end_clients to be visible to the lookup;
  // regular flow hides them.
  const { data: candidates } = useEndClients({ includeDemo: !!demoMode });
  const upload = useUploadMaterials();
  const schedule = useScheduleSession();

  const isTest = sessionType === 'online_assessment';
  const titleLabel = isTest ? 'test' : 'interview';
  const TitleLabel = isTest ? 'Test' : 'Interview';

  const [step, setStep] = useState<Step>(1);
  const [endClientId, setEndClientId] = useState(defaultEndClientId ?? '');
  const [companyName, setCompanyName] = useState('');
  const [positionTitle, setPositionTitle] = useState('');
  const [interviewDateLocal, setInterviewDateLocal] = useState(''); // datetime-local
  const [interviewNotes, setInterviewNotes] = useState('');

  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [jdMode, setJdMode] = useState<'file' | 'text'>('file');
  const [jdFile, setJdFile] = useState<File | null>(null);
  const [jdText, setJdText] = useState('');
  const [notes, setNotes] = useState('');
  const [additional, setAdditional] = useState<AdditionalMaterialDraft[]>([]);

  // Materials uploaded at step 2 → step 3 transition. Parsed in background by Gemini.
  const [uploadedMaterialIds, setUploadedMaterialIds] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const parseStatus = useMaterialsParseStatus(uploadedMaterialIds);

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ licenseKey: string; emailWarning?: string } | null>(
    null
  );

  const selectedCandidate: NeuraxEndClient | undefined = useMemo(
    () => candidates?.find((c) => c.id === endClientId),
    [candidates, endClientId]
  );

  if (!open) return null;

  function reset() {
    setStep(1);
    setEndClientId(defaultEndClientId ?? '');
    setCompanyName('');
    setPositionTitle('');
    setInterviewDateLocal('');
    setInterviewNotes('');
    setResumeFile(null);
    setJdMode('file');
    setJdFile(null);
    setJdText('');
    setNotes('');
    setAdditional([]);
    setUploadedMaterialIds([]);
    setUploading(false);
    setError(null);
    setSuccess(null);
  }

  function close() {
    if (schedule.isPending) return;
    reset();
    onClose();
  }

  function validateStep1(): string | null {
    if (!endClientId) return 'Pick a candidate.';
    if (!companyName.trim()) return 'Company name is required.';
    if (!positionTitle.trim())
      return isTest ? 'Test name is required.' : 'Role / job title is required.';
    if (!interviewDateLocal)
      return isTest ? 'Test date is required.' : 'Interview date is required.';
    return null;
  }

  function validateStep2(): string | null {
    if (!resumeFile) return "Upload the candidate's resume.";
    if (jdMode === 'file' && !jdFile) return 'Upload the job description, or switch to paste text.';
    if (jdMode === 'text' && jdText.trim().length < 30) {
      return 'Paste a longer job description (at least a few sentences).';
    }
    return validateAdditionalMaterials(additional);
  }

  async function onConfirm() {
    setError(null);
    const payload: ScheduleSessionInput = {
      endClientId,
      sessionType,
      companyName: companyName.trim(),
      positionTitle: positionTitle.trim(),
      interviewDate: new Date(interviewDateLocal).toISOString(),
      interviewNotes: interviewNotes.trim(),
      materialIds: uploadedMaterialIds,
      ...(demoMode
        ? {
            demoMode: {
              validUntilDays: demoMode.validUntilDays,
              assignedToEmployeeId: demoMode.assignedToEmployeeId,
              assignedToEmployeeEmail: demoMode.assignedToEmployeeEmail,
            },
          }
        : {}),
    };

    try {
      const out = await schedule.mutateAsync(payload);
      setSuccess({ licenseKey: out.licenseKey, emailWarning: out.emailWarning });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    }
  }

  async function nextStep() {
    setError(null);
    if (step === 1) {
      const err = validateStep1();
      if (err) return setError(err);
      setStep(2);
      return;
    }
    if (step === 2) {
      const err = validateStep2();
      if (err) return setError(err);

      // Already uploaded + all parsed → just advance
      if (uploadedMaterialIds.length > 0 && parseStatus.allParsed) {
        setStep(3);
        return;
      }

      // Need to upload first
      if (uploadedMaterialIds.length === 0) {
        setUploading(true);
        try {
          const { materialIds } = await upload.mutateAsync({
            endClientId,
            resume: { kind: 'file', file: resumeFile! },
            jobDescription:
              jdMode === 'file' && jdFile
                ? { kind: 'file', file: jdFile }
                : { kind: 'text', text: jdText.trim() },
            notes: notes.trim(),
            additional: toAdditionalMaterialInputs(additional),
          });
          setUploadedMaterialIds(materialIds);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Upload failed.');
        } finally {
          setUploading(false);
        }
        // Stay on step 2 — parse-status polling will flip allParsed once Gemini finishes
      }
    }
  }

  // Auto-advance to step 3 once parsing finishes (after upload completes on step 2)
  useEffect(() => {
    if (step === 2 && uploadedMaterialIds.length > 0 && parseStatus.allParsed) {
      setStep(3);
    }
  }, [step, uploadedMaterialIds.length, parseStatus.allParsed]);

  function prevStep() {
    setError(null);
    if (step > 1) setStep((s) => (s - 1) as Step);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Schedule ${titleLabel}`}
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={close}
        aria-hidden="true"
      />

      <div className="relative w-full max-w-2xl rounded-2xl border border-white/10 bg-neurax-midnight shadow-glow overflow-hidden">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-neurax-electric/50 via-neurax-plasma/50 to-transparent" />

        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-white/5 px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-neurax-electric/30 to-neurax-plasma/30 text-neurax-electric">
              {isTest ? <Exam className="h-5 w-5" /> : <Microphone className="h-5 w-5" />}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-neurax-electric/80">
                Schedule {titleLabel}
              </p>
              <h2 className="font-display text-lg font-semibold text-white">
                {success ? `${TitleLabel} scheduled` : `New ${titleLabel}`}
              </h2>
            </div>
          </div>
          <button
            onClick={close}
            disabled={schedule.isPending}
            className="rounded-lg p-1.5 text-muted-foreground transition hover:bg-white/5 hover:text-white disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Demo banner */}
        {demoMode && !success && (
          <div
            data-testid="wizard-demo-banner"
            className="border-b border-orange-500/20 bg-orange-500/10 px-6 py-3 text-xs text-orange-300/90"
          >
            <span className="font-semibold uppercase tracking-wider text-orange-400">
              Demo session
            </span>
            <span className="ml-2">
              Not billed and doesn&apos;t consume quota. Will be activated by{' '}
              <strong className="text-white">{demoMode.recipientLabel}</strong>.
            </span>
          </div>
        )}

        {/* Stepper */}
        {!success && (
          <div className="border-b border-white/5 bg-neurax-ink/40 px-6 py-3">
            <div className="flex items-center gap-2">
              {STEPS.map((s, i) => {
                const active = step === s.num;
                const done = step > s.num;
                return (
                  <div key={s.num} className="flex flex-1 items-center gap-2">
                    <div
                      className={cn(
                        'flex h-7 items-center gap-2 rounded-full border px-3 text-[11px] font-medium uppercase tracking-wider transition',
                        active &&
                          'border-neurax-electric/40 bg-neurax-electric/10 text-neurax-electric',
                        done && 'border-neurax-mint/40 bg-neurax-mint/10 text-neurax-mint',
                        !active && !done && 'border-white/10 bg-white/[0.02] text-muted-foreground'
                      )}
                    >
                      {done ? <Check className="h-3 w-3" /> : s.icon}
                      {s.label}
                    </div>
                    {i < STEPS.length - 1 && <div className="h-px flex-1 bg-white/5" />}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Body */}
        <div className="max-h-[60vh] overflow-y-auto px-6 py-6">
          {success ? (
            <SuccessState
              titleLabel={TitleLabel}
              licenseKey={success.licenseKey}
              emailWarning={success.emailWarning}
              onDone={() => {
                reset();
                onClose();
              }}
            />
          ) : (
            <>
              {step === 1 && (
                <DetailsStep
                  isTest={isTest}
                  candidates={candidates ?? []}
                  endClientId={endClientId}
                  setEndClientId={setEndClientId}
                  candidateLocked={Boolean(defaultEndClientId)}
                  selectedCandidate={selectedCandidate}
                  companyName={companyName}
                  setCompanyName={setCompanyName}
                  positionTitle={positionTitle}
                  setPositionTitle={setPositionTitle}
                  interviewDateLocal={interviewDateLocal}
                  setInterviewDateLocal={setInterviewDateLocal}
                  interviewNotes={interviewNotes}
                  setInterviewNotes={setInterviewNotes}
                />
              )}

              {step === 2 && (
                <MaterialsStep
                  isTest={isTest}
                  resumeFile={resumeFile}
                  setResumeFile={setResumeFile}
                  jdMode={jdMode}
                  setJdMode={setJdMode}
                  jdFile={jdFile}
                  setJdFile={setJdFile}
                  jdText={jdText}
                  setJdText={setJdText}
                  notes={notes}
                  setNotes={setNotes}
                  additional={additional}
                  setAdditional={setAdditional}
                  uploaded={uploadedMaterialIds.length > 0}
                  uploading={uploading}
                  parseEntries={parseStatus.data ?? []}
                  parsedCount={parseStatus.parsedCount}
                  totalCount={parseStatus.totalCount}
                  allParsed={parseStatus.allParsed}
                />
              )}

              {step === 3 && (
                <ConfirmStep
                  isTest={isTest}
                  selectedCandidate={selectedCandidate}
                  companyName={companyName}
                  positionTitle={positionTitle}
                  interviewDateLocal={interviewDateLocal}
                  interviewNotes={interviewNotes}
                  resumeFile={resumeFile}
                  jdMode={jdMode}
                  jdFile={jdFile}
                  jdText={jdText}
                  notes={notes}
                  additional={additional}
                  demoRecipientLabel={demoMode?.recipientLabel}
                />
              )}

              {error && (
                <div
                  role="alert"
                  className="mt-4 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive"
                >
                  <Warning className="mt-0.5 h-3.5 w-3.5" />
                  <span>{error}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!success && (
          <div className="flex items-center justify-between border-t border-white/5 bg-neurax-ink/40 px-6 py-4">
            <button
              type="button"
              onClick={step === 1 ? close : prevStep}
              disabled={schedule.isPending}
              className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm font-medium text-muted-foreground transition hover:text-white disabled:opacity-50"
            >
              {step === 1 ? (
                'Cancel'
              ) : (
                <>
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </>
              )}
            </button>

            {step < 3 ? (
              <button
                type="button"
                onClick={nextStep}
                disabled={
                  uploading ||
                  (step === 2 && uploadedMaterialIds.length > 0 && !parseStatus.allParsed)
                }
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-neurax-electric to-neurax-plasma px-4 py-2.5 text-sm font-semibold text-white shadow-glow-soft transition hover:brightness-110 disabled:opacity-60"
              >
                {uploading ? (
                  <>
                    <SpinnerGap className="h-4 w-4 animate-spin" />
                    Uploading…
                  </>
                ) : step === 2 && uploadedMaterialIds.length > 0 && !parseStatus.allParsed ? (
                  <>
                    <SpinnerGap className="h-4 w-4 animate-spin" />
                    Parsing {parseStatus.parsedCount}/{parseStatus.totalCount}…
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={onConfirm}
                disabled={schedule.isPending}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-neurax-electric to-neurax-plasma px-4 py-2.5 text-sm font-semibold text-white shadow-glow-soft transition hover:brightness-110 disabled:opacity-60"
              >
                {schedule.isPending ? (
                  <>
                    <SpinnerGap className="h-4 w-4 animate-spin" />
                    AI training in process…
                  </>
                ) : (
                  <>
                    <Sparkle className="h-4 w-4" />
                    Schedule {titleLabel}
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ───────────────────────── Step 1 ───────────────────────── */
function DetailsStep(props: {
  isTest: boolean;
  candidates: NeuraxEndClient[];
  endClientId: string;
  setEndClientId: (v: string) => void;
  candidateLocked: boolean;
  selectedCandidate: NeuraxEndClient | undefined;
  companyName: string;
  setCompanyName: (v: string) => void;
  positionTitle: string;
  setPositionTitle: (v: string) => void;
  interviewDateLocal: string;
  setInterviewDateLocal: (v: string) => void;
  interviewNotes: string;
  setInterviewNotes: (v: string) => void;
}) {
  return (
    <div className="space-y-5">
      <div className="space-y-1.5">
        <Label icon={<User className="h-3.5 w-3.5" />}>Candidate</Label>
        {props.candidateLocked && props.selectedCandidate ? (
          <div className="rounded-xl border border-neurax-electric/30 bg-neurax-electric/10 px-4 py-3">
            <p className="text-sm font-semibold text-white">{props.selectedCandidate.name}</p>
            <p className="text-xs text-muted-foreground">{props.selectedCandidate.email}</p>
          </div>
        ) : (
          <select
            value={props.endClientId}
            onChange={(e) => props.setEndClientId(e.target.value)}
            className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm text-white outline-none transition focus:border-neurax-electric/60 focus:ring-2 focus:ring-neurax-electric/30"
          >
            <option value="">Select a candidate…</option>
            {props.candidates.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} — {c.email}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FieldText
          label="Company"
          icon={<Buildings className="h-3.5 w-3.5" />}
          value={props.companyName}
          onChange={props.setCompanyName}
          placeholder="Acme Corp"
        />
        <FieldText
          label={props.isTest ? 'Test name' : 'Role / job title'}
          icon={<Briefcase className="h-3.5 w-3.5" />}
          value={props.positionTitle}
          onChange={props.setPositionTitle}
          placeholder={props.isTest ? 'Frontend take-home' : 'Senior Software Engineer'}
        />
      </div>

      <div className="space-y-1.5">
        <Label icon={<CalendarBlank className="h-3.5 w-3.5" />}>
          {props.isTest ? 'Test date / due' : 'Interview date'}
        </Label>
        <input
          type="datetime-local"
          value={props.interviewDateLocal}
          onChange={(e) => props.setInterviewDateLocal(e.target.value)}
          className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm text-white outline-none transition focus:border-neurax-electric/60 focus:ring-2 focus:ring-neurax-electric/30"
        />
      </div>

      <div className="space-y-1.5">
        <Label icon={<FileText className="h-3.5 w-3.5" />}>Internal notes (optional)</Label>
        <textarea
          rows={2}
          value={props.interviewNotes}
          onChange={(e) => props.setInterviewNotes(e.target.value)}
          placeholder={
            props.isTest
              ? 'e.g. take-home test, 48hr deadline, focus on code quality'
              : 'e.g. panel of 3, system design focus, remote'
          }
          className="w-full resize-none rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm text-white placeholder:text-muted-foreground/60 outline-none transition focus:border-neurax-electric/60 focus:ring-2 focus:ring-neurax-electric/30"
        />
        <p className="text-[11px] text-muted-foreground">
          Visible to your team. Not shown to the candidate.
        </p>
      </div>
    </div>
  );
}

/* ───────────────────────── Step 2 ───────────────────────── */
function MaterialsStep(props: {
  isTest: boolean;
  resumeFile: File | null;
  setResumeFile: (f: File | null) => void;
  jdMode: 'file' | 'text';
  setJdMode: (m: 'file' | 'text') => void;
  jdFile: File | null;
  setJdFile: (f: File | null) => void;
  jdText: string;
  setJdText: (s: string) => void;
  notes: string;
  setNotes: (s: string) => void;
  additional: AdditionalMaterialDraft[];
  setAdditional: (items: AdditionalMaterialDraft[]) => void;
  uploaded: boolean;
  uploading: boolean;
  parseEntries: MaterialParseEntry[];
  parsedCount: number;
  totalCount: number;
  allParsed: boolean;
}) {
  // After upload, swap the file pickers for a parsing-progress panel
  if (props.uploaded) {
    return (
      <ParseProgressPanel
        entries={props.parseEntries}
        parsedCount={props.parsedCount}
        totalCount={props.totalCount}
        allParsed={props.allParsed}
        isTest={props.isTest}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="mb-1 text-sm font-semibold text-white">Materials power the AI prep</p>
        <p className="text-xs text-muted-foreground">
          Resume + {props.isTest ? 'test brief' : 'job description'} are required. Add extra
          materials for sharper answers. Everything here tailors the real-time prompts during the{' '}
          {props.isTest ? 'test' : 'interview'}.
        </p>
      </div>

      <FileDropZone
        label="Resume"
        required
        accept=".pdf,.doc,.docx,.txt"
        file={props.resumeFile}
        onFile={props.setResumeFile}
        hint="PDF or DOC. The candidate's most up-to-date resume."
      />

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>{props.isTest ? 'Test brief / instructions' : 'Job description'} *</Label>
          <SourceToggle mode={props.jdMode} onChange={props.setJdMode} />
        </div>

        {props.jdMode === 'file' ? (
          <FileDropZone
            label=""
            accept=".pdf,.doc,.docx,.txt"
            file={props.jdFile}
            onFile={props.setJdFile}
            hint={props.isTest ? 'PDF/DOC of the test brief.' : 'PDF/DOC of the JD.'}
          />
        ) : (
          <div className="space-y-1">
            <textarea
              rows={6}
              value={props.jdText}
              onChange={(e) => props.setJdText(e.target.value)}
              placeholder={
                props.isTest ? 'Paste the test brief here…' : 'Paste the job description here…'
              }
              className="w-full resize-none rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-muted-foreground/60 outline-none transition focus:border-neurax-electric/60 focus:ring-2 focus:ring-neurax-electric/30"
            />
            <p className="text-[11px] text-muted-foreground">
              {props.jdText.trim().length} characters — aim for at least a few sentences.
            </p>
          </div>
        )}
      </div>

      <AdditionalMaterialsSection items={props.additional} onChange={props.setAdditional} />

      <div className="space-y-1.5">
        <Label icon={<TextT className="h-3.5 w-3.5" />}>Anything else (optional)</Label>
        <textarea
          rows={3}
          value={props.notes}
          onChange={(e) => props.setNotes(e.target.value)}
          placeholder="Quick notes for the AI: tone, things to emphasize, what to avoid, etc."
          className="w-full resize-none rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm text-white placeholder:text-muted-foreground/60 outline-none transition focus:border-neurax-electric/60 focus:ring-2 focus:ring-neurax-electric/30"
        />
      </div>
    </div>
  );
}

/* ───────────────────────── Step 3 ───────────────────────── */
function ConfirmStep(props: {
  isTest: boolean;
  selectedCandidate: NeuraxEndClient | undefined;
  companyName: string;
  positionTitle: string;
  interviewDateLocal: string;
  interviewNotes: string;
  resumeFile: File | null;
  jdMode: 'file' | 'text';
  jdFile: File | null;
  jdText: string;
  notes: string;
  additional: AdditionalMaterialDraft[];
  demoRecipientLabel?: string;
}) {
  const dateStr = props.interviewDateLocal
    ? new Date(props.interviewDateLocal).toLocaleString(undefined, {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—';

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-white/10 bg-neurax-ink/40 p-5">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
          {props.demoRecipientLabel ? 'Demo recipient' : 'Candidate'}
        </p>
        <p className="mt-1 text-base font-semibold text-white">
          {props.selectedCandidate?.name || '—'}
        </p>
        {props.demoRecipientLabel ? (
          <p className="text-xs text-muted-foreground">
            Activated by{' '}
            <span className="font-medium text-orange-300">{props.demoRecipientLabel}</span>
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">{props.selectedCandidate?.email}</p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <SummaryRow label="Company" value={props.companyName} />
        <SummaryRow label={props.isTest ? 'Test' : 'Role'} value={props.positionTitle} />
        <SummaryRow label="When" value={dateStr} />
        <SummaryRow label="Notes" value={props.interviewNotes || '—'} />
      </div>

      <div className="rounded-2xl border border-white/10 bg-neurax-ink/40 p-5">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Materials</p>
        <ul className="mt-2 space-y-1.5 text-sm text-white">
          <li className="flex items-center gap-2">
            <CheckCircle className="h-3.5 w-3.5 text-neurax-mint" />
            Resume — {props.resumeFile?.name || '—'}
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="h-3.5 w-3.5 text-neurax-mint" />
            {props.isTest ? 'Test brief' : 'Job description'} —{' '}
            {props.jdMode === 'file'
              ? props.jdFile?.name || '—'
              : `${props.jdText.length} chars pasted`}
          </li>
          {props.notes.trim() && (
            <li className="flex items-center gap-2">
              <CheckCircle className="h-3.5 w-3.5 text-neurax-mint" />
              Recruiter notes — {props.notes.length} chars
            </li>
          )}
          {props.additional.map((item) => (
            <li key={item.id} className="flex min-w-0 items-center gap-2">
              <CheckCircle className="h-3.5 w-3.5 shrink-0 text-neurax-mint" />
              <span className="truncate">
                {additionalMaterialTypeLabel(item.type)} —{' '}
                {item.title.trim() ||
                  (item.mode === 'file' ? item.file?.name : null) ||
                  additionalMaterialTypeLabel(item.type)}
                <span className="text-muted-foreground">
                  {' · '}
                  {item.mode === 'file' && item.file
                    ? formatFileSize(item.file.size)
                    : `${item.text.trim().length.toLocaleString('en-US')} chars pasted`}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-2xl border border-neurax-electric/20 bg-neurax-electric/[0.04] p-5">
        <p className="text-sm font-medium text-white">What happens next</p>
        <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
          <li>• A NeuraX license is issued (or reused) for this candidate</li>
          <li>• AI prep prompts are generated from the materials you uploaded</li>
          <li>• The candidate gets a branded email with their license key + steps</li>
        </ul>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-neurax-ink/40 p-3.5">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-1 truncate text-sm text-white">{value || '—'}</p>
    </div>
  );
}

/* ───────────────────────── Success ───────────────────────── */
function SuccessState({
  titleLabel,
  licenseKey,
  emailWarning,
  onDone,
}: {
  titleLabel: string;
  licenseKey: string;
  emailWarning?: string;
  onDone: () => void;
}) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(licenseKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  }

  const masked =
    licenseKey.length <= 8
      ? '•'.repeat(licenseKey.length)
      : `${licenseKey.slice(0, 4)}••••${licenseKey.slice(-4)}`;

  return (
    <div className="flex flex-col items-center gap-3 py-2 text-center">
      <div className="grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-neurax-mint/20 to-neurax-electric/20 text-neurax-mint shadow-glow-soft">
        <CheckCircle className="h-7 w-7" />
      </div>
      <h3 className="font-display text-xl font-semibold text-white">{titleLabel} scheduled</h3>
      <p className="max-w-md text-sm text-muted-foreground">
        Your candidate just received this email with their license key and the NeuraX download link.
        Keep a copy below for your records.
      </p>

      {/* License key block — mask + reveal + copy */}
      <div data-testid="success-license-block" className="mt-4 w-full max-w-md">
        <div className="rounded-2xl border border-neurax-electric/25 bg-black/40 p-5 shadow-glow-soft">
          <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.22em] text-neurax-electric/80">
            <Key className="h-3.5 w-3.5" />
            License key
          </div>
          <div className="mt-3 flex items-center justify-between gap-3">
            <code
              data-testid="success-license-key"
              className="font-mono text-base font-semibold tracking-wide text-white sm:text-lg"
            >
              {revealed ? licenseKey : masked}
            </code>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setRevealed((v) => !v)}
                aria-label={revealed ? 'Hide key' : 'Reveal key'}
                className="rounded-lg p-1.5 text-muted-foreground transition hover:bg-white/5 hover:text-white"
              >
                {revealed ? <EyeClosed className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </button>
              <button
                type="button"
                data-testid="success-copy-license"
                onClick={copy}
                aria-label="Copy license key"
                className="rounded-lg p-1.5 text-muted-foreground transition hover:bg-white/5 hover:text-white"
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5 text-neurax-mint" />
                ) : (
                  <CopySimple className="h-3.5 w-3.5" />
                )}
              </button>
            </div>
          </div>
          <p className="mt-3 text-[11px] text-muted-foreground">
            Candidate enters this in the NeuraX desktop app to start the {titleLabel.toLowerCase()}.
          </p>
        </div>
      </div>

      {emailWarning ? (
        <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-neurax-amber/30 bg-neurax-amber/10 px-2.5 py-1 text-[11px] text-neurax-amber">
          <Warning className="h-3 w-3" />
          Email warning: {emailWarning}
        </div>
      ) : (
        <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-neurax-mint/30 bg-neurax-mint/10 px-2.5 py-1 text-[11px] text-neurax-mint">
          <Check className="h-3 w-3" />
          Email sent to candidate
        </div>
      )}

      <button
        onClick={onDone}
        className="mt-4 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-neurax-electric to-neurax-plasma px-4 py-2.5 text-sm font-semibold text-white shadow-glow-soft transition hover:brightness-110"
      >
        Done
      </button>
    </div>
  );
}

/* ─────────────── Parse progress (step 2 after upload) ─────────────── */
function ParseProgressPanel({
  entries,
  parsedCount,
  totalCount,
  allParsed,
  isTest,
}: {
  entries: MaterialParseEntry[];
  parsedCount: number;
  totalCount: number;
  allParsed: boolean;
  isTest: boolean;
}) {
  return (
    <div className="space-y-5">
      <div>
        <p className="mb-1 text-sm font-semibold text-white">Parsing materials with AI</p>
        <p className="text-xs text-muted-foreground">
          Gemini Vision is extracting text and structure. We'll continue automatically once it's
          done (usually 10–25 seconds for a resume).
        </p>
      </div>

      <div className="rounded-2xl border border-white/10 bg-neurax-ink/40 p-4">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Progress
          </span>
          <span className="text-xs text-white">
            {parsedCount} / {totalCount} parsed
          </span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-white/5">
          <div
            className={cn(
              'h-full rounded-full bg-gradient-to-r transition-all duration-500',
              allParsed
                ? 'from-neurax-mint to-neurax-electric'
                : 'from-neurax-electric to-neurax-plasma'
            )}
            style={{ width: `${totalCount > 0 ? (parsedCount / totalCount) * 100 : 0}%` }}
          />
        </div>
      </div>

      <ul className="space-y-2">
        {entries.map((m) => (
          <li
            key={m.id}
            data-testid={`parse-entry-${m.id}`}
            className={cn(
              'flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm transition',
              m.parsed
                ? 'border-neurax-mint/30 bg-neurax-mint/[0.05]'
                : 'border-white/10 bg-white/[0.02]'
            )}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div
                className={cn(
                  'grid h-8 w-8 shrink-0 place-items-center rounded-lg',
                  m.parsed
                    ? 'bg-neurax-mint/15 text-neurax-mint'
                    : 'bg-white/[0.05] text-neurax-electric'
                )}
              >
                {m.parsed ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <SpinnerGap className="h-4 w-4 animate-spin" />
                )}
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm text-white">
                  {m.file_name || m.title || 'Pasted content'}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {m.parsed ? 'Parsed' : m.hasFile ? 'Parsing with AI…' : 'Ready'}
                </p>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {allParsed && (
        <p className="text-center text-xs text-neurax-mint">
          All set — moving to confirm the {isTest ? 'test' : 'interview'}…
        </p>
      )}
    </div>
  );
}

/* ───────────────────────── Bits ───────────────────────── */
function FieldText({
  label,
  value,
  onChange,
  placeholder,
  icon,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  icon?: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label icon={icon}>{label}</Label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm text-white placeholder:text-muted-foreground/60 outline-none transition focus:border-neurax-electric/60 focus:ring-2 focus:ring-neurax-electric/30"
      />
    </div>
  );
}
